import React, { FC,useEffect, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import LoadManagementHeader from '../Header/LoadManagementHeader';
import { LoadFilters } from '../Filter/LoadFilters';
import LoadList from '../TableList/LoadList';
import { GET_FILTERED_LOAD_MANAGEMENTS } from '../../graphql/client/queries';
import {
    GetFilteredLoadManagements,
    GetFilteredLoadManagements_getFilteredLoadManagements as Load,
} from '../../types/schemaTypes';
import { Error } from '../_common';
import { useStoreContext } from '../App/StoreProvider';
import { useQuery } from '@apollo/client/react';
import './LoadManagement.scss';
import DeleteModal from '../JobProfile/DeleteModal';
import { DELETE_LOADS } from '../../graphql/client/mutations';
import { LoadStatusEnum } from '../../types/LoadStatusEnum';

import * as LoadManagementListActions from '../../store/actions/loadManagementList';


interface SelectedLoad {
    loadId: number | null;
    boxNumber: string | null;
}

// Do not change this value unless the associate backend value has been updated - (8/22/19) BS
const PAGE_SIZE = 50;

const LoadManagement : FC = (): React.ReactElement => {
    const [selectedLoads, setSelectedLoads] = useState<SelectedLoad[]>([]);
    const [listHeight, setListHeight] = useState(window.screen.height);
    const [state, dispatch] = useStoreContext();
    const {
        loadManagementList: { filters, searchPhrase },
    } = state;
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isSearchOnLoadId, setIsSearchOnLoadId] = useState(true);

    const [showFilter, setShowFilter] = useState(false);
    const [dateRangeFilter, setDateRangeFilter] = useState<string | null>('1week');

    const abortController = React.useRef(new AbortController());
    const loadIds: any = [];
    useEffect(() => {
        dispatch(LoadManagementListActions.resetFilterState());
    }, []);

    // If delivered date is manually selected, ignore the dateRangeFilter and treat as alltime
    useEffect(() => {
        if (filters.fromDate || filters.toDate) {
            setDateRangeFilter('alltime');
        } else {
            setDateRangeFilter('1week');
        }
    }, [filters.fromDate, filters.toDate]);

    const variables = React.useMemo(()=> {
        // If delivered date is manually selected, use 'alltime', otherwise use the selected dateRangeFilter
        let finalDateFilter = '1week';
        if (filters.fromDate || filters.toDate) {
            finalDateFilter = 'alltime';
        } else if (dateRangeFilter !== null && dateRangeFilter !== undefined && dateRangeFilter !== '') {
            finalDateFilter = dateRangeFilter;
        }
        return {
        input: {
            regionIds: filters.regionIds,
            districtIds: filters.districtIds,
            customerIds: filters.customerIds,
            jobStatuses: filters.jobStatuses,
            proppants: filters.proppantIds,
            statuses: filters.statuses,
            carriers: filters.carrierIds,
            drivers: filters.driverIds,
            lofas: filters.lofaIds,
            jobIds: filters.jobIds,
            isSearchOnLoadId: searchPhrase ? isSearchOnLoadId : true,
            search: searchPhrase,
            sortOrder: '',
            atDestinationOutFrom: filters.fromDate,
            atDestinationOutTo: filters.toDate,
            dateRangeFilter: finalDateFilter,
        }, 
    }},[filters, searchPhrase, dateRangeFilter]);

    const onCheckAll = (checkedStatus: boolean, loadsList: any[]): void => {
        const result: SelectedLoad[] = [];
        if (checkedStatus === true) {
            loadsList.forEach((el: any): void => {
                if (el.status === LoadStatusEnum.CREATED || el.status === LoadStatusEnum.CREATED_NOT_SAVED) {
                    result.push({ loadId: el.loadId, boxNumber: el.boxNumber });
                }
            });
        }
        setSelectedLoads(result);
    };

    const [deleteLoad] = useMutation(DELETE_LOADS, {
        onCompleted() {
            setSelectedLoads([]);
            setShowDeleteModal(false);
        },
        refetchQueries: [
            {
                query: GET_FILTERED_LOAD_MANAGEMENTS,
                variables: {
                    input: {
                        regionIds: filters.regionIds,
                        districtIds: filters.districtIds,
                        customerIds: filters.customerIds,
                        jobStatuses: filters.jobStatuses,
                        proppants: filters.proppantIds,
                        statuses: filters.statuses,
                        carriers: filters.carrierIds,
                        drivers: filters.driverIds,
                        lofas: filters.lofaIds,
                        jobIds: filters.jobIds,
                        isSearchOnLoadId: isSearchOnLoadId,
                        search: searchPhrase,
                        sortOrder: '',
                        atDestinationOutFrom: filters.fromDate,
                        atDestinationOutTo: filters.toDate,
                        dateRangeFilter: (filters.fromDate || filters.toDate) ? 'alltime' : ((dateRangeFilter !== null && dateRangeFilter !== undefined && dateRangeFilter !== '') ? dateRangeFilter : '1week'),
                    },
                },
            },
        ],
    });

    const context = React.useMemo(()=> {return {
        fetchOptions: {
            signal: abortController.current.signal,
        },
    }},[]);

    const abortRequest = (): void => {
        abortController.current.abort();
    };

    const { data, error, loading, fetchMore, refetch } = useQuery<GetFilteredLoadManagements>(
        GET_FILTERED_LOAD_MANAGEMENTS,
        {
            variables: variables,
            pollInterval: 300000,
            context : context,
            fetchPolicy: "network-only",
            // nextFetchPolicy: "cache-first",
        },
       
    );

  

    if (error) {
        return <Error error={error} />;
    }

    const loadsList: Load[] = [];

    if (data && data.getFilteredLoadManagements && data.getFilteredLoadManagements.length !== 0) {
        data.getFilteredLoadManagements.forEach((element: any | null): void => {
            if (element !== null) {
                loadsList.push(element);
            }
        });
    }
    const offset = data && data.getFilteredLoadManagements ? data.getFilteredLoadManagements.length / PAGE_SIZE : 0;

    let isEndOfResults = false;

    if (
        offset % 1 !== 0 ||
        (!isEndOfResults &&
            data &&
            data.getFilteredLoadManagements &&
            data.getFilteredLoadManagements.length < PAGE_SIZE)
    ) {
        // If offset is not a whole number, the end of the results has been reached. (5/11/19) - KS
        isEndOfResults = true;
    }

    selectedLoads.forEach(it => {
        loadIds.push(it.loadId);
    });

    const deleteLoadIds = (): void => {
        const variables = { input: { loadIds } };
        deleteLoad({ variables });
    };

    const handleCancel = (): void => {
        setShowDeleteModal(false);
        setSelectedLoads([]);
    };

    const setListContainerHeight = (): void => {
        const filterDiv = document.getElementById('loadsFilterContainer');
        const listDiv = document.getElementById('loadsListContainer');

        let filterHeight = 0;
        // setTimeout in place to avoid animation miscalculations
        setTimeout(() => {
            if (filterDiv) {
                filterHeight = filterDiv.scrollHeight;
            }
            if (listDiv) {
                setListHeight(Math.min(filterHeight));
            }
        }, 100);
    };
    document.title = 'Load Management';
    return (
        <div className="JobLoadsManagement-Container">
            <LoadManagementHeader
                onCheckAll={checked => onCheckAll(checked, loadsList)}
                selectedLoads={selectedLoads}
                openSelectedLoads={() => {}}
                deleteSelectedLoads={() => setShowDeleteModal(true)}
                isSearchOnLoadId={isSearchOnLoadId}
                setIsSearchOnLoadId={setIsSearchOnLoadId}
                showFilter={showFilter}
                setShowFilter={setShowFilter}
                dateRangeFilter={dateRangeFilter}
                setDateRangeFilter={setDateRangeFilter}
                isDateRangeDisabled={!!(filters.fromDate || filters.toDate)}
            />
            {showDeleteModal && (
                <DeleteModal show={showDeleteModal} onCancel={handleCancel} onConfirm={deleteLoadIds} name="load" />
            )}
            <div className="JobLoadsManagement-BodyContainer">
                <div className="JobLoads-Container">
                    <div className="JobLoads-BodyContainer">
                        <LoadFilters
                            abortRequest={abortRequest}
                            onFilterToggle={setListContainerHeight}
                            showFilter={showFilter}
                            setShowFilter={setShowFilter}
                        />
                    </div>
                </div>

                <div
                    id="loadsListContainer"
                    className="JobLoadsManagement-ListContainer"
                    style={{ height: listHeight }}
                >
                    <LoadList
                        jobId={null}
                        abortRequest={abortRequest}
                        loadList={loadsList}
                        isLoading={loading}
                        isEndOfResults={isEndOfResults}
                        entries={(data && data.getFilteredLoadManagements) || []}
                        selectedLoads={selectedLoads}
                        setSelectedLoads={setSelectedLoads}
                        refetch={refetch}
                        onLoadMore={() =>
                            fetchMore({
                                variables: {
                                    offset,
                                },
                                updateQuery: (prev: any, { fetchMoreResult }: any) => {
                                    if (
                                        prev &&
                                        fetchMoreResult &&
                                        prev.getFilteredLoadManagements &&
                                        fetchMoreResult.getFilteredLoadManagements
                                    ) {
                                        return Object.assign({}, prev, {
                                            getFilteredLoadManagements: [
                                                ...prev.getFilteredLoadManagements,
                                                ...fetchMoreResult.getFilteredLoadManagements,
                                            ],
                                        });
                                    } else {
                                        return prev;
                                    }
                                },
                            })
                        }
                    />
                </div>
            </div> 
        </div>
    );
};

export default LoadManagement;
